import { useRef, useCallback } from "react";

// ─── Low-level helpers ──────────────────────────────────────────────────────

/** Load an image from a src URL with crossOrigin set. */
const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/** Download a single `<img>` element as a PNG at its natural resolution. */
const downloadImgAsPng = async (
  img: HTMLImageElement,
  filename: string,
): Promise<void> => {
  const source = await loadImage(img.src);
  const canvas = document.createElement("canvas");
  canvas.width = source.naturalWidth;
  canvas.height = source.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get canvas context");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0);
  const link = document.createElement("a");
  link.download = filename;
  link.href = canvas.toDataURL("image/png");
  link.click();
};

/** Download multiple `<img>` elements stitched side-by-side as one PNG. */
const downloadMultipleImgsAsPng = async (
  imgs: HTMLImageElement[],
  filename: string,
): Promise<void> => {
  const sources = await Promise.all(imgs.map((i) => loadImage(i.src)));
  const gap = 16;
  const totalW = sources.reduce(
    (w, s, i) => w + s.naturalWidth + (i > 0 ? gap : 0),
    0,
  );
  const maxH = Math.max(...sources.map((s) => s.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = totalW;
  canvas.height = maxH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get canvas context");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  let x = 0;
  sources.forEach((s, i) => {
    const y = (maxH - s.naturalHeight) / 2;
    ctx.drawImage(s, x, y);
    x += s.naturalWidth + (i < sources.length - 1 ? gap : 0);
  });
  const link = document.createElement("a");
  link.download = filename;
  link.href = canvas.toDataURL("image/png");
  link.click();
};

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Provides a ref to attach to a container and a download handler that finds
 * `<img>` elements inside that container and saves them as a PNG.
 *
 * @param filename  The default download filename (e.g. "molecule.png").
 * @param mode      `"single"` picks the first `<img>`, `"stitch"` combines all
 *                  `<img>` elements side-by-side into one image.
 */
export function useDownloadPng(
  filename: string = "download.png",
  mode: "single" | "stitch" = "single",
) {
  const containerRef = useRef<HTMLDivElement>(null);

  const download = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (mode === "stitch") {
        const imgs = Array.from(el.querySelectorAll("img"));
        if (imgs.length === 0) return;
        await downloadMultipleImgsAsPng(imgs, filename);
      } else {
        const img = el.querySelector("img");
        if (!img) return;
        await downloadImgAsPng(img, filename);
      }
    } catch (err) {
      console.error("Failed to download PNG", err);
    }
  }, [filename, mode]);

  return { containerRef, download } as const;
}
